import { Component, OnInit, ViewChild } from '@angular/core';
import { EditorComponent } from '../../../base/editor/editor.component';
import { AppService } from '../../../../services/app.service';
import { ActingProfileService } from '../../../../services/acting-profiles/acting-profile.service';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { BlobService } from '../../../../services/blob/blob.service';
import { ActingProfileEnvelope } from '../../../../models/net/envelope/acting-profile-envelope';
import { AvatarPrimitive } from '../../../../models/app/avatar-primitive';
import { CharacterType } from '../../../../models/data/talent-core/character-type';
import { Ethnicity } from '../../../../models/data/talent-core/ethnicity';
import { ActingProfile } from '../../../../models/data/talent-core/acting-profile';
import { ActingProfileDocument } from '../../../../models/net/envelope/documents/acting-profile-document';
import { ActingProfileCharacterType } from '../../../../models/data/talent-core/acting-profile-character-type';
import { ActingProfileEthnicity } from '../../../../models/data/talent-core/acting-profile-ethnicity';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { HistoryComponent } from '../../../fragments/history/history.component';
import { Gender } from '../../../../models/data/talent-core/gender';
import { ChipSelectorComponent } from '../../../fragments/chip-selector/chip-selector.component';
import { ResourceSearchResult } from '../../../../models/net/result/resource-search-result';

@Component({
    selector: 'app-acting-profile',
    templateUrl: './acting-profile.component.html',
    styleUrls: ['./acting-profile.component.css', '../../../../app.component.css']
})

export class ActingProfileComponent extends EditorComponent {

    public availableResources: AvatarPrimitive[] = [];
    public availableGenders: Gender[] = [];
    public availableCharacterTypes: AvatarPrimitive[] = [];
    public availableEthnicities: AvatarPrimitive[] = [];
    public selectedResourceIds: number[] = [];
    public selectedCharacterTypeIds: number[] = [];
    public selectedEthnicityIds: number[] = [];

    @ViewChild("resourceChipSelector") resourceChipSelector!: ChipSelectorComponent;
    @ViewChild("characterTypeChipSelector") characterTypeChipSelector!: ChipSelectorComponent;
    @ViewChild("ethnicitySelector") ethnicitySelector!: ChipSelectorComponent;
    @ViewChild("appHistory") appHistory!: HistoryComponent;

    public constructor(public override appService: AppService,
                       public override itemService: ActingProfileService,
                       public override route: ActivatedRoute,
                       public override location: Location,
                       public blobService: BlobService) {

        super(appService, itemService, route, location, "actingprofile", "actingprofiles");
    }

    public override initializeEnvelope = (envelope: ActingProfileEnvelope | null): void => {

        super.initializeEnvelope(envelope);

        this.itemEnvelope = new ActingProfileEnvelope(envelope);
        this.itemEnvelopeSnapshot = this.appService.cloneObject(this.itemEnvelope);

        const availableResources: AvatarPrimitive[] = (this.itemEnvelope as ActingProfileEnvelope).Resources.map((i: ResourceSearchResult) => {
            let result = new AvatarPrimitive();
            result.Id = i.ResourceId;
            result.Name = i.FirstName + " " + i.LastName;
            result.AvatarBlobPath = i.AvatarBlobPath;
            return result;
        });

        this.availableResources = availableResources;
        this.selectedResourceIds = [(this.itemEnvelope as ActingProfileEnvelope).Document.Item.ResourceId]; // this is a single item array because the control is in non-multi-select mode

        this.availableGenders = (this.itemEnvelope as ActingProfileEnvelope).Genders;

        const availableCharacterTypes: AvatarPrimitive[] = (this.itemEnvelope as ActingProfileEnvelope).CharacterTypes.map((i: CharacterType) => {
            let result = new AvatarPrimitive();
            result.Id = i.CharacterTypeId;
            result.Name = i.UID;
            result.AvatarBlobPath = this.appService.defaultImageUri;
            return result;
        });

        const availableEthnicities: AvatarPrimitive[] = (this.itemEnvelope as ActingProfileEnvelope).Ethnicities.map((i: Ethnicity) => {
            let result = new AvatarPrimitive();
            result.Id = i.EthnicityId;
            result.Name = i.UID;
            result.AvatarBlobPath = this.appService.defaultImageUri;
            return result;
        });

        this.availableCharacterTypes = availableCharacterTypes;
        this.availableEthnicities = availableEthnicities;

        const actingProfileId: number = (this.itemEnvelope?.Document as ActingProfileDocument).Item.ActingProfileId;

        this.selectedCharacterTypeIds = (this.itemEnvelope?.Document as ActingProfileDocument).CharacterTypes.filter((i: ActingProfileCharacterType) => i.ActingProfileId == actingProfileId).map(i => i.CharacterTypeId);
        this.selectedEthnicityIds = (this.itemEnvelope?.Document as ActingProfileDocument).Ethnicities.filter((i: ActingProfileEthnicity) => i.ActingProfileId == actingProfileId).map(i => i.EthnicityId);
    }

    public ngAfterViewInit(): void {

        this.resourceChipSelector.itemAdded.subscribe((item: AvatarPrimitive) => {

            this.selectedResourceIds = [item.Id];

            const document: ActingProfileDocument | undefined = this.itemEnvelope?.Document as ActingProfileDocument;

            if (document) {

                document.Item.ResourceId = item.Id;
            }
        });

        this.resourceChipSelector.itemRemoved.subscribe((item: AvatarPrimitive) => {

            this.selectedResourceIds = [];

            const document: ActingProfileDocument | undefined = this.itemEnvelope?.Document as ActingProfileDocument;

            if (document) {

                document.Item.ResourceId = -1;
            }
        });

        this.characterTypeChipSelector.itemAdded.subscribe((item: AvatarPrimitive) => {

            this.addCharacterTypeToActingProfile(item.Id);
        });

        this.characterTypeChipSelector.itemRemoved.subscribe((item: AvatarPrimitive) => {

            this.removeCharacterTypeFromActingProfile(item.Id);
        });

        this.ethnicitySelector.itemAdded.subscribe((item: AvatarPrimitive) => {

            this.addEthnicityToActingProfile(item.Id);
        });

        this.ethnicitySelector.itemRemoved.subscribe((item: AvatarPrimitive) => {

            this.removeEthnicityFromActingProfile(item.Id);
        });
    }

    public onTabChange(event: MatTabChangeEvent) {

        if (event.index == 1 && !this.isRevisionListLoaded) {

            this.appHistory.searchRevisions();
        }
    }

    public addCharacterTypeToActingProfile = (characterTypeId: number): void => {

        const newActingProfileCharacterType: ActingProfileCharacterType = new ActingProfileCharacterType();

        if (this.itemEnvelope?.Document) {

            newActingProfileCharacterType.ActingProfileId = (this.itemEnvelope.Document as ActingProfileDocument).Item.ActingProfileId;
            newActingProfileCharacterType.CharacterTypeId = characterTypeId;

            (this.itemEnvelope.Document as ActingProfileDocument).CharacterTypes.push(newActingProfileCharacterType);
        }
    }

    public removeCharacterTypeFromActingProfile = (characterTypeId: number): void => {

        if (this.itemEnvelope?.Document) {

            const document: ActingProfileDocument = this.itemEnvelope.Document as ActingProfileDocument;
            const actingProfileId: number = document.Item.ActingProfileId;
            const actingProfileCharacterTypes: ActingProfileCharacterType[] = (this.itemEnvelope?.Document as ActingProfileDocument).CharacterTypes.filter((i: ActingProfileCharacterType) => i.ActingProfileId == actingProfileId && i.CharacterTypeId == characterTypeId);

            const itemIndex: number | undefined = document.CharacterTypes.map((i: ActingProfileCharacterType) => i.ActingProfileCharacterTypeId).indexOf(actingProfileCharacterTypes[0].ActingProfileCharacterTypeId, 0);

            if (itemIndex != undefined && itemIndex > -1) {

                document.CharacterTypes.splice(itemIndex, 1);
            }
        }
    }

    public addEthnicityToActingProfile = (ethnicityId: number): void => {

        const newActingProfileEthnicity: ActingProfileEthnicity = new ActingProfileEthnicity();

        if (this.itemEnvelope?.Document) {

            newActingProfileEthnicity.ActingProfileId = (this.itemEnvelope.Document as ActingProfileDocument).Item.ActingProfileId;
            newActingProfileEthnicity.EthnicityId = ethnicityId;

            (this.itemEnvelope.Document as ActingProfileDocument).Ethnicities.push(newActingProfileEthnicity);
        }
    }

    public removeEthnicityFromActingProfile = (ethnicityId: number): void => {

        if (this.itemEnvelope?.Document) {

            const document: ActingProfileDocument = this.itemEnvelope.Document as ActingProfileDocument;
            const actingProfileId: number = document.Item.ActingProfileId;
            const actingProfileEthnicitys: ActingProfileEthnicity[] = (this.itemEnvelope?.Document as ActingProfileDocument).Ethnicities.filter((i: ActingProfileEthnicity) => i.ActingProfileId == actingProfileId && i.EthnicityId == ethnicityId);

            const itemIndex: number | undefined = document.Ethnicities.map((i: ActingProfileEthnicity) => i.ActingProfileEthnicityId).indexOf(actingProfileEthnicitys[0].ActingProfileEthnicityId, 0);

            if (itemIndex != undefined && itemIndex > -1) {

                document.Ethnicities.splice(itemIndex, 1);
            }
        }
    }
}
